import { memo } from "react";
import { useHistory } from "react-router-dom";

import Grid2 from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";

import { FAVOURITES_ICON_SOURCE , RECIPE } from "../app/constants/constants";
import { useAppSelector } from "../app/store/hooks";
import useLogRecipeEdit from "../hooks/useLogRecipeEdit";
import { ServingSizeIcon, TimeDotIcon } from "../iconRef/recipePageIcons";
import { EventLogsEnum } from "../utils/events";
import { resizeImageIfCloudinary } from "../utils/images/cloudinaryUtils";
import { logEvents } from "../utils/logging";
import Favorites from "./favourites/favouritesView";
import RatingDisplay from "./ratingDisplay";
import styles from './recipeCard.module.css';
import { CookTime, PrepTime, Tags } from './recipeCardSubComponents';

const getCardWidth = (source: string): string => {
    if (source === 'explore') {
        return "47%";
    }

    return "170px";
};

export const ServingSize = memo(({ servingSize }: { servingSize: number }) => {
    if (!servingSize) return null;
    return <>
        <div className={styles.time_dot_icon}>
            <TimeDotIcon />
        </div>
        <div className={styles.serving_size_container}>
            <ServingSizeIcon />
            <div className={styles.serving_size_text}>
                <Typography variant='tagsAndTimeAndServingSize'>{servingSize}</Typography>
            </div>
        </div>
    </>
})

export const RecipeName = memo(({ recipeName }: { recipeName: string }) => <div className={styles.recipe_name}>
        <Typography variant="recipeName">{recipeName}</Typography>
    </div>)

const RecipeCard = ({ recipe, source = 'explore', homePageRowType, homePageRow, viewedHomePageRow, viewedHomePageColumn }: { recipe: any, source?: string, homePageRowType?: string, homePageRow?: number, viewedHomePageRow?: number, viewedHomePageColumn?: number }) => {
    const user = useAppSelector((state) => state.main.user);
    const device = useAppSelector((state) => state.main.device);
    const newUrl = resizeImageIfCloudinary(recipe?.metadata?.url, 170, -1, -1, -1, true);
    const history = useHistory();
    const rating = source !== "explore" ? recipe?.metadata?.rating : recipe?.rating;
    const { logRecipeEdit } = useLogRecipeEdit();

    const ratingCount = source !== "explore" ? recipe?.metadata?.ratingCount : recipe?.ratingCount;
    const ratingVisible = !!(rating && ratingCount && rating > 3.5 && ratingCount > 10);

    const sourceCategories = source !== "explore" ? recipe?.metadata?.categories : recipe?.metadata?.diet;
    const categories = Array.isArray(sourceCategories) ? sourceCategories : [];
    const tags = source !== "explore" ? recipe?.metadata?.tags : recipe?.metadata?.cuisine;
    const recipeName = recipe?.metadata?.name;
    const cookTime = recipe?.metadata?.cookTime || recipe?.metadata?.cookingTime || null;
    const prepTime = recipe?.metadata?.prepTime ?? 0
    const servingSize = recipe?.metadata?.maxServing > recipe?.metadata?.minServing
        ? `${recipe?.metadata?.minServing}-${recipe?.metadata?.maxServing}`
        : recipe?.metadata?.maxServing;
    const cardWidth = getCardWidth(source);

    const handleRecipeOpen = () => {
        if (source !== "explore") {
            history.push(`/${RECIPE}/${recipe?.id}`);
            const sessionId: any = sessionStorage.getItem("sessionId");
            logEvents(sessionId, EventLogsEnum.APP_RECIPE_OPENED, {
                userId: user?.id,
                deviceId: device?.displayName ?? device?._id,
                recipeId: recipe?.id,
                review_visible: ratingVisible,
                source,
                ...(source === 'homepage' && { homePageRow, viewedHomePageRow, viewedHomePageColumn, homePageRowType }),
            });
        } else {
            history.push(`/recipe/${recipe?.id}`);
            logRecipeEdit(EventLogsEnum.APP_RECIPE_OPENED, {
                eventData: { recipeId: recipe.id, review_visible: ratingVisible, source: "explore" },
            });
        }
    };

    return (
        <div
            className={styles.recipe_card}
            onClick={() => handleRecipeOpen()}
            style={{ width: cardWidth }}
        >
            <img
                alt={recipe?.metadata?.name}
                className={styles.recipe_image}
                loading="lazy"
                src={newUrl}
            />
            <Grid2 className={styles.recipe_card_abs}>
                <Favorites
                    recipeId={recipe.id}
                    size={26}
                    source={FAVOURITES_ICON_SOURCE.RECIPE_CARD}
                    stroke="none"
                />
            </Grid2>

            <div className={styles.recipe_footer}>
                <div className={styles.tags_and_rating}>
                    <div className={styles.tags_and_categories}>
                        <Tags category={categories[0]} recipeName={recipe?.metadata?.name} tags={tags} />
                    </div>
                    {ratingVisible ? <RatingDisplay rating={rating} /> : null}
                </div>
                <RecipeName recipeName={recipeName} />
                <div className={styles.time_and_serving_size}>
                            <PrepTime prepTime={prepTime} />                       
                    <CookTime cookTime={cookTime} />

                    <ServingSize servingSize={servingSize} />
                </div>
            </div>
        </div>

    );
};

export default RecipeCard;
